import { defaultLocale, locales } from "./i18n-config";
import Cookies from "js-cookie";

const LOCALE_COOKIE = "NEXT_LOCALE";
const COOKIE_EXPIRES_DAYS = 365;

// Get the root domain for setting cookies across subdomains
export function getCookieDomain(hostname?: string): string | undefined {
    if (typeof window === "undefined" && hostname === undefined) return undefined;

    const host = hostname ?? window.location.hostname;

    // For localhost, IPv4 and IPv6 hosts, don't set domain
    if (host === "localhost" || host.startsWith("127.") || host.startsWith("192.") || host.includes(":")) {
        return undefined;
    }

    // Extract root domain (e.g., "victorinbox.pbxlocal.test" -> ".pbxlocal.test")
    const parts = host.split(".");
    if (parts.length > 2) {
        // Return root domain with leading dot for cross-subdomain access
        return "." + parts.slice(-2).join(".");
    }

    // For domains like "example.com", use ".example.com"
    return "." + host;
}

function getAllLocaleCookieValues(): string[] {
    if (typeof document === "undefined") return [];
    return document.cookie
        .split("; ")
        .filter((part) => part.startsWith(`${LOCALE_COOKIE}=`))
        .map((part) => {
            // A malformed value planted on .pbx.im by any subdomain must not
            // crash every app that parses the cookie
            try {
                return decodeURIComponent(part.slice(LOCALE_COOKIE.length + 1));
            } catch {
                return "";
            }
        })
        .filter(Boolean);
}

function writeDomainCookie(locale: string) {
    Cookies.set(LOCALE_COOKIE, locale, {
        expires: COOKIE_EXPIRES_DAYS,
        path: "/",
        domain: getCookieDomain(),
    });
}

// The pre-Vite app (and next-intl) wrote NEXT_LOCALE as a host-only cookie with a
// 1-year max-age. Host-only cookies are listed before the newer ".pbx.im" domain
// cookie and shadow it on every read, so users kept seeing their old language.
// Drop the host-only variant on every read/write; if it was the only cookie,
// migrate its value to domain scope so the preference survives.
function readLocaleCookie(): string | undefined {
    const values = getAllLocaleCookieValues();
    if (values.length === 0) {
        return undefined;
    }

    Cookies.remove(LOCALE_COOKIE, { path: "/" });

    const domainValue = Cookies.get(LOCALE_COOKIE);
    if (domainValue) {
        return domainValue;
    }

    const legacyValue = values[0];
    if (!locales.includes(legacyValue as any)) {
        return undefined;
    }
    writeDomainCookie(legacyValue);
    return legacyValue;
}

export function isLocaleCookiePersisted(locale: string): boolean {
    return Cookies.get(LOCALE_COOKIE) === locale;
}

export function detectLocale(): string {
    // 1. Check cookie
    const cookieLocale = readLocaleCookie();
    if (cookieLocale && locales.includes(cookieLocale as any)) {
        return cookieLocale;
    }

    // 2. Check URL parameter (if using locale in URL)
    if (typeof window !== "undefined") {
        const pathname = window.location.pathname;
        const urlLocale = locales.find((locale) => pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`);
        if (urlLocale) {
            setLocaleCookie(urlLocale);
            return urlLocale;
        }
    }

    // 3. Check browser language
    if (typeof navigator !== "undefined") {
        const browserLang = navigator.language.split("-")[0];
        const matchedLocale = locales.find((locale) => locale === browserLang);
        if (matchedLocale) {
            setLocaleCookie(matchedLocale);
            return matchedLocale;
        }
    }

    // 4. Default
    return defaultLocale;
}

export function setLocaleCookie(locale: string) {
    if (locales.includes(locale as any)) {
        Cookies.remove(LOCALE_COOKIE, { path: "/" });
        writeDomainCookie(locale);
    }
}

export function setLocale(locale: string) {
    setLocaleCookie(locale);

    // Reload to apply new locale
    if (typeof window !== "undefined") {
        window.location.reload();
    }
}

export function getLocale(): string {
    const locale = Cookies.get(LOCALE_COOKIE);
    return locale && locales.includes(locale as any) ? locale : defaultLocale;
}
